import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    CreateCustomerGroupRequest,
    CreateCustomerRequest,
    Customer,
    ImportCustomersParams,
    ListCustomersParams,
    UpdateCustomerGroupRequest,
    UpdateCustomerRequest,
} from '@/lib/api/customer-api'
import { m } from '@/lib/paraglide/messages'
import { customerApi } from '@/lib/api/customer-api'

const INFINITE_PAGE_SIZE = 20

export const customerKeys = {
    all: ['customers'] as const,
    lists: () => [...customerKeys.all, 'list'] as const,
    list: (params: ListCustomersParams) =>
        [...customerKeys.lists(), params] as const,
    detail: (id: string) => [...customerKeys.all, 'detail', id] as const,
    orders: (id: string) => [...customerKeys.all, 'orders', id] as const,
    debts: (id: string) => [...customerKeys.all, 'debts', id] as const,
    creditStatus: (id: string) =>
        [...customerKeys.all, 'credit-status', id] as const,
    groups: () => ['customer-groups'] as const,
    infinite: (search: string) =>
        [...customerKeys.lists(), 'infinite', search] as const,
}

interface InfinitePage {
    items: Customer[]
    meta:
        | {
              page: number
              per_page: number
              total: number
              total_pages: number
          }
        | undefined
}

export function useInfiniteCustomersQuery(search: string) {
    return useInfiniteQuery({
        queryKey: customerKeys.infinite(search),
        queryFn: async ({ pageParam = 1 }): Promise<InfinitePage> => {
            const res = await customerApi.list({
                page: pageParam,
                per_page: INFINITE_PAGE_SIZE,
                search: search || undefined,
            })
            return { items: res.data ?? [], meta: res.meta }
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            if (!lastPage.meta) return undefined
            return lastPage.meta.page < lastPage.meta.total_pages
                ? lastPage.meta.page + 1
                : undefined
        },
    })
}

export function useCustomerGroups() {
    return useQuery({
        queryKey: customerKeys.groups(),
        queryFn: async () => {
            const res = await customerApi.listGroups()
            return res.data ?? []
        },
    })
}

export function useCustomers(params?: ListCustomersParams) {
    return useQuery({
        queryKey: customerKeys.list(params ?? {}),
        queryFn: async () => {
            const res = await customerApi.list(params)
            return { data: res.data ?? [], meta: res.meta }
        },
    })
}

export function useCustomer(id: string) {
    return useQuery({
        queryKey: customerKeys.detail(id),
        queryFn: async () => {
            const res = await customerApi.getById(id)
            return res.data ?? null
        },
        enabled: Boolean(id),
    })
}

export function useCustomerOrders(
    id: string,
    params?: { page?: number; per_page?: number },
) {
    return useQuery({
        queryKey: customerKeys.orders(id),
        queryFn: async () => {
            const res = await customerApi.getOrders(id, params)
            return { data: res.data ?? [], meta: res.meta }
        },
        enabled: Boolean(id),
    })
}

export function useCustomerDebts(id: string, params?: { status?: string }) {
    return useQuery({
        queryKey: customerKeys.debts(id),
        queryFn: async () => {
            const res = await customerApi.listDebts(id, params)
            return { data: res.data ?? [], meta: res.meta }
        },
        enabled: Boolean(id),
    })
}

export function useCustomerCreditStatus(id: string) {
    return useQuery({
        queryKey: customerKeys.creditStatus(id),
        queryFn: async () => {
            const res = await customerApi.getCreditStatus(id)
            return res.data ?? null
        },
        enabled: Boolean(id),
    })
}

export function useCreateCustomer() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: CreateCustomerRequest) => customerApi.create(data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: customerKeys.lists() })
            toast.success(m.toast_customer_create_success())
        },
        // Lỗi validate BE (400/409 PHONE_EXISTS) do form tự xử qua
        // applyCustomerCreateError để hiện inline dưới field + link khách trùng.
    })
}

export function useUpdateCustomer(id: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: UpdateCustomerRequest) =>
            customerApi.update(id, data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: customerKeys.lists() })
            void qc.invalidateQueries({ queryKey: customerKeys.detail(id) })
            toast.success(m.toast_customer_update_success())
        },
        // Lỗi (409 PHONE_EXISTS, validate…) do CustomerForm tự hiện cạnh field
        // hoặc toast qua applyCustomerCreateError — không toast trùng ở đây.
    })
}

export function useDeleteCustomer() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => customerApi.delete(id),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: customerKeys.lists() })
            toast.success(m.toast_customer_delete_success())
        },
        onError: () => toast.error(m.toast_customer_delete_error()),
    })
}

export function useCreateCustomerGroup() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: CreateCustomerGroupRequest) =>
            customerApi.createGroup(data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: customerKeys.groups() })
            toast.success(m.toast_customer_group_success())
        },
        onError: () => toast.error(m.toast_customer_group_error()),
    })
}

export function useUpdateCustomerGroup() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string
            data: UpdateCustomerGroupRequest
        }) => customerApi.updateGroup(id, data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: customerKeys.groups() })
            toast.success(m.toast_customer_group_update_success())
        },
        onError: () => toast.error(m.toast_customer_group_error()),
    })
}

export function useDeleteCustomerGroup() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => customerApi.deleteGroup(id),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: customerKeys.groups() })
            toast.success(m.toast_customer_group_delete_success())
        },
        onError: () => toast.error(m.toast_customer_group_error()),
    })
}

export function useImportCustomers() {
    return useMutation({
        mutationFn: (params: ImportCustomersParams) =>
            customerApi.importExcel(params),
    })
}

/** KiotViet sync status (enabled flag + last run); refetches after a trigger. */
export function useKiotVietSyncStatus() {
    return useQuery({
        queryKey: [...customerKeys.all, 'kiotviet-sync-status'] as const,
        queryFn: async () => {
            const res = await customerApi.getKiotVietSyncStatus()
            return res.data ?? null
        },
        retry: false,
    })
}

export function useTriggerKiotVietSync() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: () => customerApi.triggerKiotVietSync(),
        onSuccess: () => {
            void qc.invalidateQueries({
                queryKey: [...customerKeys.all, 'kiotviet-sync-status'],
            })
        },
    })
}
